import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router";
import { useTranslation } from 'react-i18next';
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import Loading from "../../components/Loading";
import BarValue from "../../components/Bars/BarValue"
import { scrollTop } from "../../helpers/scrolling";
import iconLeft from "url:shared-assets/icons/white/arrow-left.svg"
import iconRight from "url:shared-assets/icons/black/arrow-right.svg"
import iconStress from "url:shared-assets/icons/white/stress.svg";
import iconAi from "url:shared-assets/icons/white/ai.svg";
import ProgressBar from "../../components/Progress/ProgressBar";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import {getLanguage} from "../../lang/helpers";
import Api from "../../services/Api";
import { todayStr } from "../../helpers/dateFunctions";
import {storeAiStart} from "../../helpers/ai_thread";

interface Question {
  en: string
  sv: string
  positive: boolean
}

interface Choice {
  en: string
  sv: string
  score: number
}

const questions: Array<Question> = [
  {
    en: "In the last month, how often have you been upset because of something that happened unexpectedly?",
    sv: "Under den senaste månaden, hur ofta har du blivit upprörd över något som hände oväntat?",
    positive: false,
  },
  {
    en: "In the last month, how often have you felt that you were unable to control the important things in your life?",
    sv: "Under den senaste månaden, hur ofta har du känt att du inte kunde kontrollera de viktiga sakerna i ditt liv?",
    positive: false,
  },
  {
    en: "In the last month, how often have you felt nervous and stressed?",
    sv: "Under den senaste månaden, hur ofta har du känt dig nervös och stressad?",
    positive: false,
  },
  {
    en: "In the last month, how often have you felt confident about your ability to handle your personal problems?",
    sv: "Under den senaste månaden, hur ofta har du känt dig trygg i din förmåga att hantera dina personliga problem?",
    positive: true,
  },
  {
    en: "In the last month, how often have you felt that things were going your way?",
    sv: "Under den senaste månaden, hur ofta har du känt att saker och ting har gått din väg?",
    positive: true,
  },
  {
    en: "In the last month, how often have you found that you could not cope with all the things that you had to do?",
    sv: "Under den senaste månaden, hur ofta har du känt att du inte klarade av allt du behövde göra?",
    positive: false,
  },
  {
    en: "In the last month, how often have you been able to control irritations in your life?",
    sv: "Under den senaste månaden, hur ofta har du kunnat hantera irritationer i ditt liv?",
    positive: true,
  },
  {
    en: "In the last month, how often have you felt that you were on top of things?",
    sv: "Under den senaste månaden, hur ofta har du känt att du hade kontroll och överblick?",
    positive: true,
  },
  {
    en: "In the last month, how often have you been angered because of things that happened that were outside of your control?",
    sv: "Under den senaste månaden, hur ofta har du blivit arg över saker som hände utanför din kontroll?",
    positive: false,
  },
  {
    en: "In the last month, how often have you felt difficulties were piling up so high that you could not overcome them?",
    sv: "Under den senaste månaden, hur ofta har du känt att svårigheterna hopade sig så mycket att du inte kunde hantera dem?",
    positive: false,
  }
]

const choices: Array<Choice> = [
  {
    en: "Never",
    sv: "Aldrig",
    score: 0
  },
  {
    en: "Almost never",
    sv: "Nästan aldrig",
    score: 1
  },
  {
    en: "Sometimes",
    sv: "Ibland",
    score: 2
  },
  {
    en: "Fairly often",
    sv: "Ganska ofta",
    score: 3
  },
  {
    en: "Very often",
    sv: "Mycket ofta",
    score: 4
  }
]

interface Answer {
  score: number
}

interface ClientInterface {
  id: string;
  firstName: string;
  lastName: string;
}

export default function StressTest() {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Answer[]>([]);
  const { stressId } = useParams();
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language)
  const [loaded, setLoaded] = useState<boolean>(false);
  const [pageNr, setPageNr] = useState(1);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(-1);
  const [showResult, setShowResult] = useState(false);
  const [changed, setChanged] = useState(false);
  const [created, setCreated] = useState(new Date());
  const pages = Math.ceil(questions.length / 3);
  const [calculatingResult, setCalculatingResult] = useState(false);
  const [originId, setOriginId] = useState<string | null>(null);
  const [client, setClient] = useState<ClientInterface | null>(null);

  useEffect(() => {
    let url = stressId ? "coach/stress/" + stressId : "client/profile/stress";
    api.get(url).then((response) => {
      const storedAnswers: Array<Answer> | null = response.json.answers
      if (storedAnswers) {
        setAnswers(storedAnswers);
        setOriginId(response.json.id);
        let answered = 0
        for (const a of storedAnswers) {
          if (a.score < 0) {
            break;
          }
          answered++;
        }
        setResult(response.json.result)
        setClient(response.json.client)
        if (response.json.created) {
          setCreated(new Date(response.json.created));
        }
        let _pageNr = Math.ceil(answered / 3) + 1
        if (_pageNr > 4) _pageNr = 4
        setPageNr(_pageNr)
        setShowResult(answered === questions.length && response.json.result > -1);
      } else {
        const _answers: Array<Answer> = [];
        for (const q of questions) {
          _answers.push({score: -1})
        }
        setAnswers(_answers)
      }
      setLoaded(true)
    });
  }, []);

  const retakeTest = () => {
    api.get("client/profile/stress?retake=1").then((response) => {
      const _answers: Array<Answer> = [];
      for (const q of questions) {
        _answers.push({score: -1})
      }
      setAnswers(_answers);
      setPageNr(1);
      setShowResult(false);
    });
  }

  const pageDone = () => {
    let answered = 0
    for (const a of answers) {
      if (a.score < 0) {
        break;
      }
      answered++;
    }
    if (answered === answers.length) {
      return true;
    }
    return Math.floor(answered / 3) >= pageNr;

  }

  const setPage = (step: number) => {
    if (step === 1) {
      if (changed) {
        setSending(true);
        const lastAnswers = pageNr > (pages - 1)
        let fakePause = 1;
        let _result = -1;
        if (lastAnswers) {
          _result = calculateResults();
          setCalculatingResult(true);
          fakePause = 3000;
        }
        const data = {
          answers: answers,
          result: _result
        }
        api.post("client/profile/stress", data).then(() => {
          setTimeout(() => {
            setSending(false);
            if (lastAnswers) {
              setShowResult(true);
            } else {
              setPageNr(pageNr + step)
            }
            setCalculatingResult(false);
            scrollTop();
          }, fakePause);
        });
      } else {
        if (pageNr > (pages - 1)) {
          setShowResult(true);
        } else {
          setPageNr(pageNr + step)
        }
        scrollTop();
      }
      setChanged(false);

    } else {
      if (pageNr === 1) {
        navigate("/profile")
      } else {
        setPageNr(pageNr + step);
        scrollTop();
      }
    }

  }

  const selectOption = (questionNr: number, score: number) => {
    const _answers = [...answers];
    _answers[questionNr].score = score;
    setAnswers(_answers);
    setChanged(true);
  }

  if (!loaded) {
    return <Loading />;
  }

  if (calculatingResult) {
    return <Loading txt={lang === "sv" ? "Beräknar din stressnivå..." : "Calculating your stress level..."}/>;
  }

  const calculateResults = () => {

    let totalScore = 0;
    for (let i = 0; i < questions.length; i++) {
      if (answers[i].score > -1) {
        const question: Question = questions[i]
        const score = question.positive ? 4 - answers[i].score : answers[i].score
        totalScore += score
      }
    }

    return totalScore;

  }

  const getStressLevel = (category: number) => {
    if (lang === "sv") {
      if (category === 2) return "Hög"
      else if (category === 1) return "Måttlig"
      else return "Låg"
    } else {
      if (category === 2) return "High"
      else if (category === 1) return "Moderate"
      else return "Low"
    }
  }

  const getStressRange = (category: number) => {
    if (category === 2) return "27-40"
    else if (category === 1) return "14-26"
    else return "0-13"
  }

  const renderStressAnalysis = (category: number) => {
    if (lang === "sv") {
      if (category === 2) {
        return (
          <>
            <p>
              Din självskattning tyder på en hög nivå av upplevd stress. Du kan uppleva att kraven är många och att
              trycket påverkar fokus, välmående och beslut. Det kan vara hjälpsamt att sakta ner, skilja på vad som
              är mest brådskande och mest viktigt, och ta små steg för att återfå kontroll. Du kan använda din AI-coach,
              eller kanske kollegor eller någon du litar på för att utforska hur du kan skapa mer hållbara rutiner för
              återhämtning och balans.
            </p>
          </>
          )
      } else if (category === 1) {
        return (
          <>
            <p>
              Dina svar visar en måttlig nivå av upplevd stress. Du hanterar vardagens krav, men stress kan ibland
              påverka humör, fokus eller energi. Fundera över vilka situationer som oftast triggar stress hos dig. Små
              justeringar – som tydligare gränser, korta återhämtningspauser eller bättre sömn – kan göra stor skillnad.
              Om stressen pågår under längre tid kan en mer strukturerad reflektion eller kortare coachande samtal
              hjälpa dig att stärka dina strategier.
            </p>
          </>
        )
      } else {
        return (
          <>
            <p>
              Din självskattning visar en låg nivå av upplevd stress. Du verkar ha en bra balans i vardagen och
              strategier som hjälper dig att hantera påfrestningar på ett effektivt sätt. Fortsätt att värna om de
              rutiner som stärker din återhämtning, sömn och fokus. Genom att uppmärksamma tidiga tecken på stress kan
              du bevara balansen över tid.
            </p>
          </>
        )
      }
    } else {
      if (category === 2) {
        return (
          <>
            <p>
              Your self-assessment points to a high level of perceived stress. You may feel overloaded or under
              pressure in several areas of life, which can affect focus, health, and decision-making.
            </p>
            <p className="mt-4">
              It can help to slow down, identify what feels most urgent versus most important, and create micro-steps
              to regain control. If you wish, you can use your AI Coach, peers, or a trusted person to explore how
              to re-establish sustainable routines for recovery and resilience.
            </p>
          </>
        )
      } else if (category === 1) {
        return (
          <>
            <p>
              Your self-reported answers indicate a moderate level of perceived stress. You’re managing everyday
              demands, though stress may occasionally affect mood, focus, or energy.
            </p>
            <p className="mt-4">
              Reflect on what typically triggers stress for you. Even small shifts—like clearer boundaries, short
              recovery breaks, or better sleep quality—can make a measurable difference.
              If you notice ongoing stress, structured reflection or short coaching sessions may help you strengthen
              coping strategies.
            </p>
          </>
        )
      } else {
        return (
          <>
            <p>
              Your self-assessment suggests a relatively low level of perceived stress. You appear to experience
              balance and effective coping strategies in daily life. Continue maintaining routines that support
              recovery, sleep, and focus. Staying aware of early stress signals helps you preserve that balance
              over time.
            </p>
          </>
        )
      }
    }
  }

  const renderNextSteps = (category: number) => {
    if (lang === "sv") {
      if (category === 2) {
        return (
          <>
            <p>
              Reflektera med din AI-coach
            </p>
            <p>
             – Beskriv hur din situation ser ut just nu och få stöd i att identifiera vad
              som känns mest krävande, samt hur du kan bryta ner det i hanterbara steg.
            </p>
            <p className="mt-4">
              Skapa en återhämtningsplan
            </p>
            <p>
              – Välj en konkret, stärkande handling inom det närmaste dygnet – till exempel
              att delegera en uppgift, ta en ordentlig paus eller kontakta någon du litar på.
            </p>
          </>
        )
      } else if (category === 1) {
        return (
          <>
            <p>
              Reflektera med din AI-coach
            </p>
            <p>
              – Vilka situationer eller mönster skapar mest stress för dig?
            </p>
            <p className="mt-4">
              Skapa en handling
            </p>
            <p >
              – Gör din insikt konkret genom ett praktiskt steg, till exempel planera in
              återhämtning eller skapa tydligare ramar för arbete och fritid.
            </p>
          </>
        )
      } else {
        return (
          <>
            <p>
              Reflektera med din AI-coach
            </p>
            <p>
              – Utforska vad som hjälper dig att behålla lugn och balans, även
              under mer intensiva perioder.
            </p>
          </>
        )
      }
    } else {
      if (category === 2) {
        return (
          <>
            <p>
              Reflect with your AI Coach
            </p>
            <p>
              – Describe your current situation and get guidance to identify what
              feels most draining and break it down into manageable steps.
            </p>
            <p className="mt-4">
              Create a recovery plan
            </p>
            <p>
              – Set one concrete, self-supporting action for the next 24 hours
              (e.g., delegate one task, take a proper break, or connect with someone you trust).
            </p>
          </>
        )
      } else if (category === 1) {
        return (
          <>
            <p>
              Reflect with your AI Coach
            </p>
            <p>
              – What situations or patterns increase your stress?
            </p>
            <p className="mt-4">
              Create an action
            </p>
            <p className="mt-2">
              – Turn your insights into one practical next step, such as planning
              recovery breaks or setting clearer boundaries.
            </p>
          </>
        )
      } else {
        return (
          <>
            <p>
              Reflect with your AI Coach
            </p>
            <p>
              – Explore what helps you stay balanced and how to maintain that even
              during busier periods.
            </p>
          </>
        )
      }
    }
  }

  const toAiThread = () => {
    let message: string;
    if (lang === "sv") {
      message = "Jag vill utforska vilka situationer eller mönster som ökar min stress";
    } else {
      message = "I want to research what situations or patterns increase my stress";
    }

    storeAiStart("coaching", "stress", null, null, null, message, "stress", originId);
    navigate("/ai/0");
  };

  const renderResult = () => {

    const score = calculateResults();
    let stressCategory = 0;
    if (score > 26) {
stressCategory = 2;
    } else if (score > 13) {
      stressCategory = 1;
    }

    return (
      <>
        <div className="flex mt-4">
          <div className="rounded-md bg-indigo-900 p-2 border-indigo-800 border-1">
            <img src={iconStress} className="h-10 w-10" alt="brain"/>
          </div>
          <div className="ml-3">
            <div className="z-heading1">
              {lang === "sv" ? "Stress test - resultat" : "Stress test result"}
            </div>
            <div className="text-sm text-gray-400">
              {lang === "sv" ? "Skapad" : "Created"}&nbsp;
              {todayStr(created)}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-indigo-900 rounded-md p-4 border-indigo-800 border-1">
          <div className="text-center">
            <div className="flex justify-center items-center">
              <div className="z-display1">{score}</div>
              <div className="text-gray-400">&nbsp;/&nbsp;40</div>
            </div>
            <div className="mt-4">
              {lang === "sv" ? "Stressnivå" : "Stress level"}: {getStressLevel(stressCategory)}
            </div>
          </div>

          <BarValue size={"large"} value={score} min={0} max={40} results={score + ""} className="mt-6"/>


          <div className="flex justify-between mt-4 text-sm text-gray-400">
            <div>0 ({lang === "sv" ? "Ingen stress" : "No stress"})</div>
            <div>20</div>
            <div>40 ({lang === "sv" ? "Hög stress" : "High stress"})</div>
          </div>

          <hr className="border-indigo-700 mt-4"/>

          <div className="mt-4 font-medium">
            {getStressLevel(stressCategory)} stress&nbsp;({getStressRange(stressCategory)})
          </div>

          <div className="text-gray-400">
            {renderStressAnalysis(stressCategory)}
          </div>

          <hr className="border-indigo-700 mt-4"/>

          <div className="mt-4 font-medium">
            {lang === "sv" ? "Förslag på nästa steg:" : "Suggested next steps:"}
          </div>

          <div className="text-gray-400">
            {renderNextSteps(stressCategory)}
          </div>

          {
            !stressId &&
              <CustomButton buttonType="outlineWhiteBase" className="mt-4 flex items-center justify-center" onClick={() => toAiThread()}
                            leftIcon={<img src={iconAi} className="h-4 w-4" alt="ai"/>}>
                {lang === "sv" ? "Reflektera med AI-coachen" : "Reflect with AI Coach"}
              </CustomButton>
          }

        </div>

        <div className="mt-6 z-title1">
          {lang === "sv"? "Förstå ditt resultat" : "Understanding your score"}
        </div>

        <div className="mt-4 bg-indigo-900 rounded-md p-4 border-indigo-800 border-1">
          <div className="font-medium">
            {lang === "sv" ? "Resultatintervall" : "Score range"}
          </div>

          <div className="mt-4 text-gray-400 flex justify-between">
            <p>{getStressLevel(0)} stress</p>
            <p>{getStressRange(0)}</p>
          </div>

          <hr className="mt-2 border-indigo-700"/>

          <div className="mt-2 text-gray-400 flex justify-between">
            <p>{getStressLevel(1)} stress</p>
            <p>{getStressRange(1)}</p>
          </div>

          <hr className="mt-2 border-indigo-700"/>

          <div className="mt-2 text-gray-400 flex justify-between">
            <p>{getStressLevel(2)} stress</p>
            <p>{getStressRange(2)}</p>
          </div>

          <hr className="mt-2 border-indigo-700"/>

          <div className="mt-4 font-medium">
            {lang === "sv" ? "Om PSS" : "About the PSS"}
          </div>

          <div className="text-gray-400">
            {
              lang === "sv" ? "Perceived Stress Scale (PSS) är ett av de mest använda psykologiska instrumenten för att mäta upplevd stress. Det mäter i vilken grad situationer i ditt liv har uppfattats som stressande under den senaste månaden. Frågorna undersöker hur oförutsägbart, okontrollerbart och överbelastat du upplever ditt liv." :
                "The Perceived Stress Scale (PSS) is a widely used psychological instrument for measuring the perception of stress. It measures the degree to which situations in your life are appraised as stressful over the past month. Questions assess how unpredictable, uncontrollable, and overloaded respondents find their lives."
            }
          </div>
        </div>

        {
          !stressId &&
            <CustomButton buttonType="outlineWhiteBase" className="mt-4" onClick={() => retakeTest()}>
              {lang === "sv" ? "Gör om testet" : "Retake Assessment"}
            </CustomButton>
        }
      </>
    )
  }

  let crumbs;
  if (client) {
    crumbs = [
      {name: lang === "sv" ? "Hem" : "Home", url: "/"},
      {name: t("my-clients"), url: "/clients"},
      {name: client.firstName + " " + client.lastName, url: "/clients/" + client.id},
      {name: "Stress test"},
    ]
  } else {
    crumbs = [
      {name: lang === "sv" ? "Profil" : "Profile", url: "/profile"},
      {name: "Stress test"},
    ]
  }

  if (showResult) {
    return (
      <div className="main-content">
        <LocalBreadcrumbs crumbs={crumbs}/>
        {renderResult()}
      </div>
    )
  }



  return (
    <div className="main-content">
      <LocalBreadcrumbs crumbs={crumbs}/>
      <div className="mt-4">
        <ProgressBar step={pageNr} steps={pages}/>
      </div>
      {
        questions.slice((pageNr - 1) * 3, pageNr * 3).map((question, questionNr) => {
          const _questionNr = questionNr + (pageNr - 1) * 3
          return (
            <div key={"question_" + _questionNr} className="mt-6">
              <div className="z-title1">
                {lang === "sv" ? question.sv : question.en}
              </div>
              {
                choices.map(choice => {
                  const _selectedScore = answers[_questionNr].score
                  return (
              <div className="mt-2" key={"a_" + _questionNr + '_' + choice.score}>
                        <RadioButton
                          id={"a_" + _questionNr + '_' + choice.score}
                          name={"a_" + _questionNr + '_' + choice.score}
                          label={lang === "sv" ? choice.sv : choice.en}
                          value={"" + choice.score}
                          checked={_selectedScore === choice.score}
                          onChange={() => selectOption(_questionNr, choice.score)} // Set to 1 for "A lot"
                        />
                </div>
                  )
                })
              }
            </div>
          )
        })
      }

      <div className="flex justify-between mt-6">
        <div className="w-40">
          <CustomButton buttonType="outlineWhiteBase" onClick={() => setPage(-1)}
                        leftIcon={<img src={iconLeft} alt="left"/>}>
            {pageNr < 2 ? lang === "sv" ? "Tillbaka till profil" : "Back to Profile" :
              lang === "sv" ? "Föregående" : "Previous"}
          </CustomButton>
        </div>

        <div className="w-40">
          <CustomButton disabled={sending || !pageDone()} onClick={() => setPage(1)}
                        rightIcon={<img src={iconRight} alt="left"/>}>
            {pages === pageNr ? t('submit') : lang === "sv" ? "Nästa" : "Next"}
          </CustomButton>
        </div>

      </div>
    </div>
  )

}